import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Globe, Copy, CheckCircle2, AlertCircle, Trash2, Shield, Star } from "lucide-react";

export default function DomainWizard({ website }) {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/domains/mine?website_id=${website.website_id}`).then(r => setDomains(r.data));
  useEffect(() => { load(); }, [website.website_id]);

  const connect = async () => {
    if (!newDomain.trim()) return toast.error("Enter a domain");
    setBusy(true);
    try {
      await api.post("/domains/connect", { website_id: website.website_id, domain: newDomain.trim() });
      setNewDomain(""); load();
      toast.success("Domain added — now add DNS records");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const verify = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/domains/${id}/verify`);
      if (r.data.verified) toast.success("Verified! SSL provisioning…");
      else toast.error(r.data.last_reason || "Not verified yet");
      load();
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Remove this domain?")) return;
    await api.delete(`/domains/${id}`); load();
  };

  const makePrimary = async (id) => {
    try { await api.post(`/domains/${id}/primary`); toast.success("Primary set"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div className="max-w-3xl space-y-5">
      {/* OxyCom subdomain */}
      <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50">
        <div className="flex items-center gap-2 text-xs font-black uppercase text-emerald-700"><CheckCircle2 className="w-4 h-4"/>OxyCom Subdomain — Active</div>
        <div className="mt-1 font-mono font-bold text-lg">{website.subdomain}</div>
        <div className="text-xs text-slate-500 mt-1">This URL always works and has SSL enabled automatically.</div>
      </div>

      {/* Connect new domain */}
      <Card className="p-5">
        <h3 className="font-bold flex items-center gap-2"><Globe className="w-5 h-5 text-sky-500"/>Connect a Custom Domain</h3>
        <p className="text-xs text-slate-500 mt-1">Bring your own domain like <span className="font-mono">www.yourbrand.com</span>. Free SSL is auto-provisioned after verification.</p>
        <div className="mt-3 flex gap-2">
          <Input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="www.yourdomain.com" data-testid="new-domain"/>
          <Button onClick={connect} disabled={busy} className="bg-sky-500 hover:bg-sky-600" data-testid="connect-domain">Connect</Button>
        </div>
      </Card>

      {/* Domain list */}
      {domains.map(d => (
        <Card key={d.domain_id} className="p-5" data-testid={`domain-${d.domain_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-lg">{d.domain}</span>
                {d.primary && <Badge className="bg-amber-100 text-amber-700"><Star className="w-3 h-3 mr-1"/>Primary</Badge>}
                {d.verified ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1"/>Verified</Badge>
                            : <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1"/>Pending DNS</Badge>}
                {d.ssl_status === "provisioning" && <Badge className="bg-sky-100 text-sky-700"><Shield className="w-3 h-3 mr-1"/>SSL Provisioning</Badge>}
                {d.ssl_status === "pending" && !d.verified && <Badge variant="outline">SSL Pending</Badge>}
              </div>
            </div>
            <div className="flex gap-2">
              {d.verified && !d.primary && <Button size="sm" variant="outline" onClick={() => makePrimary(d.domain_id)}>Set Primary</Button>}
              <Button size="sm" variant="outline" onClick={() => verify(d.domain_id)} disabled={busy} data-testid={`verify-${d.domain_id}`}>Verify</Button>
              <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => del(d.domain_id)}><Trash2 className="w-3 h-3"/></Button>
            </div>
          </div>

          {!d.verified && (
            <div className="mt-3 p-4 bg-slate-50 rounded-lg border">
              <div className="text-xs font-black uppercase text-slate-600 mb-3">DNS Records — add these at your registrar</div>
              <table className="w-full text-xs">
                <thead className="text-slate-500">
                  <tr className="text-left">
                    <th className="py-2">Type</th><th>Host</th><th>Value</th><th></th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  <tr className="border-t">
                    <td className="py-2 font-bold">TXT</td>
                    <td>_oxycom</td>
                    <td className="break-all">{d.verify_token}</td>
                    <td><button onClick={() => copy(d.verify_token)} className="text-sky-600"><Copy className="w-3 h-3"/></button></td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 font-bold">CNAME</td>
                    <td>www (or @)</td>
                    <td>{d.cname_target}</td>
                    <td><button onClick={() => copy(d.cname_target)} className="text-sky-600"><Copy className="w-3 h-3"/></button></td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 font-bold">A</td>
                    <td>@</td>
                    <td>{d.a_record}</td>
                    <td><button onClick={() => copy(d.a_record)} className="text-sky-600"><Copy className="w-3 h-3"/></button></td>
                  </tr>
                </tbody>
              </table>
              {d.last_reason && <div className="mt-2 text-xs text-rose-600">⚠️ {d.last_reason}</div>}
              <div className="mt-2 text-[11px] text-slate-500">DNS propagation can take 5 minutes to 24 hours. After adding, click <b>Verify</b>.</div>
            </div>
          )}

          {d.verified && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-bn">
              ✅ Domain সফলভাবে verify হয়েছে। SSL certificate auto-provision হবে (৫–১০ মিনিট)।
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
